/**
 * SSE (Server-Sent Events) parsing and stream pumping utilities.
 * Pure TypeScript — no React, no fetch, no DOM dependencies.
 */

export interface SSEEvent {
  event?: string
  data: string
  id?: string
}

/**
 * Parse a chunk of SSE-formatted text into zero or more SSEEvents.
 *
 * SSE wire format:
 *   - Lines beginning with 'data:' accumulate data
 *   - Lines beginning with 'event:' set the event name
 *   - Lines beginning with 'id:' set the event id
 *   - A blank line terminates one event and emits it (if it has any data)
 *   - Lines without a recognised prefix are silently skipped
 */
export function parseSSEChunk(chunk: string): SSEEvent[] {
  const events: SSEEvent[] = []
  const lines = chunk.split('\n')

  let data: string[] = []
  let event: string | undefined
  let id: string | undefined

  for (const raw of lines) {
    const line = raw.trimEnd() // remove trailing CR if present

    if (line === '') {
      // Blank line → dispatch event if we accumulated any data
      if (data.length > 0) {
        events.push({
          data: data.join('\n'),
          ...(event !== undefined && { event }),
          ...(id !== undefined && { id }),
        })
      }
      // Reset accumulators
      data = []
      event = undefined
      id = undefined
      continue
    }

    const colonIdx = line.indexOf(':')
    if (colonIdx === -1) {
      // No colon → malformed line, skip
      continue
    }

    const field = line.slice(0, colonIdx)
    // Value: strip exactly one leading space if present
    const value = line.slice(colonIdx + 1).replace(/^ /, '')

    if (field === 'data') {
      data.push(value)
    } else if (field === 'event') {
      event = value
    } else if (field === 'id') {
      id = value
    }
    // Other fields (e.g. 'retry', comment ':') are ignored
  }

  // Emit any unterminated event at end of chunk (no trailing blank line)
  if (data.length > 0) {
    events.push({
      data: data.join('\n'),
      ...(event !== undefined && { event }),
      ...(id !== undefined && { id }),
    })
  }

  return events
}

/**
 * Pump a ReadableStream of Uint8Array chunks, decode each chunk as UTF-8,
 * parse SSE events, and invoke onEvent for every complete event.
 *
 * Handles partial chunks by buffering text across reads and only dispatching
 * events on double-newline (blank-line) boundaries.
 *
 * Resolves when the stream is exhausted ({ done: true }).
 */
export async function pumpStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (e: SSEEvent) => void,
): Promise<void> {
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()

    if (done) {
      // Flush any remaining buffered text as a final parse
      if (buffer.trim()) {
        parseSSEChunk(buffer).forEach(onEvent)
      }
      return
    }

    buffer += decoder.decode(value, { stream: true })

    // Process all complete events (terminated by \n\n or \r\n\r\n)
    // We split on double-newline boundaries and keep the last (possibly partial) segment.
    const parts = buffer.split(/\n\n|\r\n\r\n/)
    // All parts except the last are complete event blocks
    const complete = parts.slice(0, -1)
    buffer = parts[parts.length - 1] ?? ''

    for (const block of complete) {
      // Re-append the trailing newline that split consumed so parseSSEChunk
      // sees a blank-line terminator and emits the event.
      parseSSEChunk(block + '\n\n').forEach(onEvent)
    }
  }
}
